import { FC, useEffect, useMemo, useRef, useState, type CSSProperties, type SVGProps } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import {
  ResponsiveContainer,
  Tooltip,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LabelList,
  XAxis,
  YAxis,
  CartesianGrid,
  TooltipProps,
  LineChart,
  Line,
} from "recharts";
import {
  ChevronDown,
  ChevronUp,
  Download,
  Eye,
  ExternalLink,
  FileText,
  Heart,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import LoadingSpinner from "src/components/reusable/LoadingSpinner";
import PostMediaThumb from "./PostMediaThumb";
import {
  DefinitionTooltip,
  compareDashboardValues,
  DashboardDataTablePanel,
  DashboardDataTablePanelHeader,
  DashboardDataTableSearch,
  DashboardNarrativePanel,
  DashboardSectionHeader,
  DashboardExportButton,
  DashboardDataTableScroll,
  DashboardDataTableSortButton,
  DashboardDataTableTable,
  TablePagePagination,
  toggleTableSort,
  dashboardDataTable as dt,
  type SortDirection,
} from "src/components/common";
import { useDashboardNarrative } from "src/hooks/useDashboardNarrative";
import { useDashboardPdfRowOverride } from "src/hooks/useDashboardPdfTableExpansion";
import {
  useInstagramPostCaptions,
  useInstagramContentReport,
} from "../../hooks/useOrganicMediaData";
import {
  formatOrganicContentKpiTitle,
  formatOrganicPostTypeLabel,
  getOrganicContentKpiDefinitionInfo,
  getOrganicContentSectionDefinitionInfo,
  getOrganicEngagementDefinitionInfo,
  getOrganicPlatformMetricDefinitionInfo,
} from "./organicMediaDefinitions";
import OrganicDateRangePicker from "./OrganicDateRangePicker";
import OrganicSelectMenu from "./OrganicSelectMenu";
import { normalizeApiLang } from "src/utils/apiLang";
import { useLanguage } from "src/hooks/useLanguage";
import { resolveDashboardChangePercent } from "src/utils/dashboardChangeFallback";
import { formatNumber, toArabicIndicDigitsInString, wrapBidiIsolate } from "src/utils/numberFormat";
import type { OrganicDateFilters } from "./FacebookContentReport";
import {
  CHART_COLOR_PALETTE,
  CHART_PRIMARY_COLOR,
  CHART_SECONDARY_COLOR,
} from "src/components/charts/chartPalette";
import { downloadCsv } from "src/dashboards/paidMedia/utils/paidMediaCsvExport";
import type { TrendPoint } from "src/services/OrganicMediaAPIs";

type MetricKey = "engagementRate" | "organicReach" | "impressions";

const PERCENT_METRICS: MetricKey[] = ["engagementRate"];
type TrendGranularity = "daily" | "monthly";

// A "%" (or other literal punctuation) glued onto formatNumber's return value
// sits outside its internal bidi isolate, so it can still be reordered by the
// browser's bidi algorithm in RTL Arabic text. Re-isolate the composed string.
const isArabicLanguage = (language?: string) => Boolean(language?.toLowerCase().startsWith("ar"));
const withBidiIsolate = (text: string, language?: string) =>
  isArabicLanguage(language) ? wrapBidiIsolate(text) : text;

type DateRange = {
  startDate: string;
  endDate: string;
};

type PercentAxisConfig = {
  domain?: [number, number];
  ticks?: number[];
};

type PostTableSortKey =
  | "caption"
  | "postType"
  | "impressions"
  | "reach"
  | "likes"
  | "comments"
  | "shares"
  | "saves"
  | "engagementRate";

const truncateCaption = (text: string, maxLen = 50) =>
  text.length <= maxLen ? text : `${text.slice(0, maxLen)}...`;

const toTitleCase = (text: string) =>
  text
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .trim()
    .split(/\s+/)
    .map(word => (word ? `${word.charAt(0).toUpperCase()}${word.slice(1)}` : word))
    .join(" ");

const getCaptionPreview = (text: string) => truncateCaption(text, 300);

const KPI_ICON_MAP: Record<string, typeof FileText> = {
  posts: FileText,
  reach: Eye,
  engagement: Heart,
  follower: FileText,
};

const ORGANIC_BLUE_PRIMARY = CHART_PRIMARY_COLOR;
const ORGANIC_BLUE_SECONDARY = CHART_SECONDARY_COLOR;
const ORGANIC_CHART_COLORS = CHART_COLOR_PALETTE;
const CONTENT_BREAKDOWN_COLORS = [
  "#2f66ed",
  "#31d0bd",
  "#a855f7",
  "#5b9cf2",
  "#18bf74",
  "#f59e0b",
  "#8b5cf6",
  "#14b8a6",
] as const;
const POSTS_PER_PAGE = 5;
const CONTENT_TYPE_BAR_PAGE_SIZE = 5;

// Title is always sourced from the backend; this only normalizes casing
// (capitalizes the first letter of each word) so styling never diverges
// from whatever name the backend returns.
const formatKpiTitle = (title: string, t: TFunction) =>
  formatOrganicContentKpiTitle(title, t, toTitleCase);

const wrapLocalizedPostTypeLabel = (
  value: string,
  t: TFunction,
  wordsPerLine = 1,
  maxLines = 4
) => {
  const words = formatOrganicPostTypeLabel(value, t).split(/\s+/).filter(Boolean);
  const lines: string[] = [];

  for (let index = 0; index < words.length; index += wordsPerLine) {
    if (lines.length === maxLines - 1) {
      lines.push(words.slice(index).join(" "));
      break;
    }
    lines.push(words.slice(index, index + wordsPerLine).join(" "));
  }

  return lines;
};

function ContentTypeAxisTick(props: SVGProps<SVGTextElement> & {
  x?: number;
  y?: number;
  payload?: { value: string };
}) {
  const { t } = useTranslation();
  const { x = 0, y = 0, payload } = props;
  const lines = wrapLocalizedPostTypeLabel(payload?.value ?? "", t);

  return (
    <g transform={`translate(${x},${y + 12})`}>
      <text
        x={0}
        y={0}
        dy={0}
        textAnchor="middle"
        fill="#334155"
        fontSize={12}
      >
        {lines.map((line, index) => (
          <tspan key={`${line}-${index}`} x={0} dy={index === 0 ? 0 : 13}>
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );
}

const formatTrendMetricLabel = (value: string, t: TFunction) => {
  const trimmed = value.trim();
  const normalized = trimmed.toLowerCase().replace(/[_\s]+/g, " ");
  if (normalized === "organic reach" || trimmed === "الوصول العضوي") {
    return t("organicMedia.instagram.reach");
  }
  // The "Posts Created" trend series is surfaced as "Viewers" in the chart.
  if (
    normalized === "posts created" ||
    trimmed === "المنشورات التي تم إنشاؤها" ||
    trimmed === "المشاركات التي تم إنشاؤها"
  ) {
    return t("organicMedia.instagram.viewers");
  }

  return formatKpiTitle(value, t);
};

const INSTAGRAM_REACTION_KEYS = ["likes"];

const formatInstagramEngagementLabel = (metricKey: string, t: TFunction) => {
  if (metricKey === "reactions") {
    return t("organicMedia.instagram.reactions");
  }
  // Falls back to the raw backend key (title-cased) when no translation exists,
  // so a renamed/new metric key from the backend still shows a sensible label
  // instead of a raw i18n key or a stale hardcoded name.
  const translated = t(`organicMedia.instagram.engagementMetrics.${metricKey}`, {
    defaultValue: "",
  });
  return translated || toTitleCase(metricKey);
};

const parseDateValueToMs = (value: string, includeEndOfDay = false) => {
  const raw = String(value ?? "").trim();
  if (!raw) {return Number.NaN;}

  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    const year = Number(isoMatch[1]);
    const month = Number(isoMatch[2]) - 1;
    const day = Number(isoMatch[3]);
    const ms = Date.UTC(year, month, day);
    return includeEndOfDay ? ms + 24 * 60 * 60 * 1000 - 1 : ms;
  }

  const usMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (usMatch) {
    const month = Number(usMatch[1]) - 1;
    const day = Number(usMatch[2]);
    const year = Number(usMatch[3]);
    const ms = Date.UTC(year, month, day);
    return includeEndOfDay ? ms + 24 * 60 * 60 * 1000 - 1 : ms;
  }

  const parsed = new Date(raw);
  return Number.isNaN(parsed.getTime()) ? Number.NaN : parsed.getTime();
};

const getMonthBucket = (value: string) => {
  const raw = String(value ?? "").trim();
  const isoMatch = raw.match(/^(\d{4})-(\d{2})/);
  if (isoMatch) {
    return `${isoMatch[1]}-${isoMatch[2]}`;
  }

  const usMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (usMatch) {
    return `${usMatch[3]}-${String(Number(usMatch[1])).padStart(2, "0")}`;
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return raw;
  }
  return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, "0")}`;
};

const formatTrendDateByGranularity = (
  value: string | number,
  granularity: TrendGranularity,
  language?: string
) => {
  const raw = String(value ?? "");
  if (!raw) {return "";}

  const localizeDigits = (text: string) =>
    isArabicLanguage(language) ? toArabicIndicDigitsInString(text) : text;

  if (granularity === "monthly") {
    const monthMatch = raw.match(/^(\d{4})-(\d{2})$/);
    if (monthMatch) {
      return localizeDigits(`${monthMatch[2]}-${monthMatch[1]}`);
    }
  }

  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return localizeDigits(`${isoMatch[3]}-${isoMatch[2]}-${isoMatch[1]}`);
  }

  const usMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (usMatch) {
    const d = String(usMatch[2]).padStart(2, "0");
    const m = String(usMatch[1]).padStart(2, "0");
    return localizeDigits(`${d}-${m}-${usMatch[3]}`);
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {return localizeDigits(raw);}
  const dd = String(parsed.getDate()).padStart(2, "0");
  const mm = String(parsed.getMonth() + 1).padStart(2, "0");
  return localizeDigits(`${dd}-${mm}-${parsed.getFullYear()}`);
};

interface InstagramContentReportProps {
  dateFilters: OrganicDateFilters;
}

type EngagementDetailRow = {
  metricKey: string;
  value: number;
  /** Pre-localized sibling of `value`; absent when the backend hasn't sent it yet. */
  valueFormattedValue?: string;
  /** Omitted when the API does not return period-over-period change. */
  percentageChange?: number;
  /** Pre-localized sibling of `percentageChange`; mirrors its nullability. */
  percentageChangeFormattedValue?: string | null;
  comparisonLabel: string;
  trend: TrendPoint[];
};

type EngagementCardRow = EngagementDetailRow & {
  details?: EngagementDetailRow[];
};

function DonutTooltip({ active, payload }: TooltipProps<number, string>) {
  const { t, i18n } = useTranslation();
  if (!active || !payload?.length) {
    return null;
  }

  const p = payload[0];
  // Recharts populates `percent` (0–1) on pie tooltip entries at runtime, but it
  // isn't declared on the Payload type — read it defensively.
  const percent = (p as { percent?: number }).percent;
  // Recharts always attaches the original datum (from `chartPostsByType`) under
  // `payload`, so the backend's pre-localized sibling is available here too.
  const valueFormattedValue = (p.payload as { valueFormattedValue?: string } | undefined)
    ?.valueFormattedValue;
  return (
    <div className="organic-tooltip organic-tooltip--kpi">
      <div className="organic-tooltip__bar" />
      <div className="organic-tooltip__content">
        <div className="organic-tooltip__meta-line">
          <span className="organic-tooltip__meta-label">{t("organicMedia.tooltips.postType")}</span>
          <strong className="organic-tooltip__meta-value">
            {formatOrganicPostTypeLabel(String(p.name), t)}
          </strong>
        </div>
        <div className="organic-tooltip__meta-line">
          <span className="organic-tooltip__meta-label">{t("organicMedia.tooltips.totalPosts")}</span>
          <strong className="organic-tooltip__meta-value">
            {valueFormattedValue ?? formatNumber(Number(p.value), {}, i18n.language)}
            {typeof percent === "number"
              ? withBidiIsolate(
                  ` (${formatNumber(percent * 100, { minimumFractionDigits: 1, maximumFractionDigits: 1 }, i18n.language)}%)`,
                  i18n.language
                )
              : ""}
          </strong>
        </div>
      </div>
    </div>
  );
}

function HorizontalBarTooltip({
  active,
  payload,
  metricKey,
}: TooltipProps<number, string> & { metricKey: MetricKey }) {
  const { t, i18n } = useTranslation();
  if (!active || !payload?.length) {
    return null;
  }

  const row = payload[0]?.payload as
    | { type: string; valueFormattedValue?: string }
    | undefined;
  const value = Number(payload[0]?.value ?? 0);
  const isPercentMetric = PERCENT_METRICS.includes(metricKey as MetricKey);
  const formattedValue =
    row?.valueFormattedValue ??
    (isPercentMetric
      ? withBidiIsolate(
          `${formatNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 }, i18n.language)}%`,
          i18n.language
        )
      : formatNumber(value, {}, i18n.language));

  return (
    <div className="organic-tooltip organic-tooltip--kpi">
      <div className="organic-tooltip__bar" />
      <div className="organic-tooltip__content">
        <div className="organic-tooltip__meta-line">
          <span className="organic-tooltip__meta-label">{t("organicMedia.tooltips.postType")}</span>
          <strong className="organic-tooltip__meta-value">
            {row?.type ? formatOrganicPostTypeLabel(row.type, t) : "-"}
          </strong>
        </div>
        <div className="organic-tooltip__meta-line">
          <span className="organic-tooltip__meta-label">{t("organicMedia.tooltips.metricValue")}</span>
          <strong className="organic-tooltip__meta-value">
            {formattedValue}
          </strong>
        </div>
      </div>
    </div>
  );
}

function OverTimeTooltip({ active, label, payload }: TooltipProps<number, string>) {
  const { t, i18n } = useTranslation();
  if (!active || !payload?.length) {
    return null;
  }

  const labelText = formatTrendDateByGranularity(
    String(label ?? ""),
    /^\d{4}-\d{2}$/.test(String(label ?? "")) ? "monthly" : "daily",
    i18n.language
  );
  const nonZeroRows = payload.filter(entry => Number(entry?.value ?? 0) > 0);
  const rowsToRender = nonZeroRows.length > 0 ? nonZeroRows : payload.slice(0, 1);

  return (
    <div className="organic-tooltip organic-tooltip--kpi">
      <div className="organic-tooltip__bar" />
      <div className="organic-tooltip__content">
        <div className="organic-tooltip__meta-line">
          <span className="organic-tooltip__meta-label">{t("organicMedia.tooltips.date")}</span>
          <strong className="organic-tooltip__meta-value">{labelText}</strong>
        </div>
        {rowsToRender.map(entry => (
          <div className="organic-tooltip__meta-line" key={String(entry.name)}>
            <span className="organic-tooltip__meta-label">
              {formatTrendMetricLabel(String(entry.name), t)}:
            </span>
            <strong className="organic-tooltip__meta-value">
              {formatNumber(Number(entry.value ?? 0), {}, i18n.language)}
            </strong>
          </div>
        ))}
      </div>
    </div>
  );
}

// Trend lines mixing a 0–1-ish percentage metric (Engagement Rate) with a
// raw-count metric (Impressions/Reach/Viewers) need separate Y axes — sharing
// one linear axis squishes the percentage line flat against zero.

const isPercentTrendCard = (card: { metricKey: string }) => {
  const normalized = card.metricKey;
  return normalized.includes("engagement") || normalized.includes("ctr");
};

const getTrendState = (pct: number): "positive" | "negative" | "neutral" => {
  if (pct === 0) {return "neutral";}
  return pct > 0 ? "positive" : "negative";
};

function formatBarMetricTick(metricKey: MetricKey, value: number | string, language: string) {
  const numericValue = Number(value ?? 0);
  if (PERCENT_METRICS.includes(metricKey)) {
    return withBidiIsolate(
      `${formatNumber(numericValue, { minimumFractionDigits: 2, maximumFractionDigits: 2 }, language)}%`,
      language
    );
  }
  return formatNumber(numericValue, {}, language);
}

// Looks up the pre-localized sibling for whichever metric is currently
// selected on a `postTypeMetrics` row.
function getPostTypeMetricFormattedValue(
  row: {
    engagementRateFormattedValue?: string;
    organicReachFormattedValue?: string;
    impressionsFormattedValue?: string;
  },
  metricKey: MetricKey
): string | undefined {
  switch (metricKey) {
    case "engagementRate":
      return row.engagementRateFormattedValue;
    case "organicReach":
      return row.organicReachFormattedValue;
    case "impressions":
      return row.impressionsFormattedValue;
    default:
      return undefined;
  }
}

function getPercentAxisConfig(values: number[]): PercentAxisConfig {
  const maxValue = Math.max(0, ...values.map(value => Number(value ?? 0)));
  const step = maxValue <= 2 ? 0.5 : maxValue <= 5 ? 1 : 2;
  const upperBound = Math.max(step, Math.ceil(maxValue / step) * step);
  const tickCount = Math.max(2, Math.round(upperBound / step));
  const ticks = Array.from({ length: tickCount + 1 }, (_, index) =>
    Number((index * step).toFixed(2))
  );

  return {
    domain: [0, upperBound] as [number, number],
    ticks,
  };
}

const InstagramContentReport: FC<InstagramContentReportProps> = ({
  dateFilters,
}) => {
  const { t, i18n } = useTranslation();
  const { isRTL } = useLanguage();
  const apiLang = normalizeApiLang(i18n.language);
  const {
    appliedStartDate,
    appliedEndDate,
    draftStartDate,
    draftEndDate,
    onApplyDates,
  } = dateFilters;
  const [postCaption, setPostCaption] = useState("(All)");
  const [selectedMetric, setSelectedMetric] = useState<MetricKey>(
    "engagementRate"
  );
  const [trendGranularity, setTrendGranularity] = useState<TrendGranularity>("daily");
  const [contentTypeBarPage, setContentTypeBarPage] = useState(1);
  const [currentPostPage, setCurrentPostPage] = useState(1);
  const [postTableSearch, setPostTableSearch] = useState("");
  const [postTableSort, setPostTableSort] = useState<{
    key: PostTableSortKey;
    direction: SortDirection;
  } | null>(null);
  const [isReactionMenuOpen, setIsReactionMenuOpen] = useState(false);
  const reactionMenuRef = useRef<HTMLDivElement | null>(null);
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const [isPdfCapturing, setIsPdfCapturing] = useState(false);

  const { postCaptions } = useInstagramPostCaptions(
    appliedStartDate,
    appliedEndDate,
    apiLang,
  );
  const {
    kpis,
    totalEngagements,
    reactionsPercentageChange,
    reactionsPercentageChangeFormattedValue,
    postsByType,
    postTypeMetrics,
    postDetails,
    loading,
    error,
  } = useInstagramContentReport(
    appliedStartDate,
    appliedEndDate,
    postCaption,
    apiLang,
    trendGranularity,
  );

  const igContentNarrativePayload = useMemo<Record<string, unknown>>(
    () => ({
      kpis,
      total_engagements: totalEngagements,
      posts_by_type: postsByType,
      post_type_metrics: postTypeMetrics,
      post_details: postDetails.slice(0, 20),
      trends_kpi: { daily: kpis.flatMap(k => k.trend), monthly: [] },
    }),
    [kpis, totalEngagements, postsByType, postTypeMetrics, postDetails],
  );

  const { narrative: igContentNarrative, loading: igContentNarrativeLoading, aiSummary } = useDashboardNarrative({
    dashboardKey: "organic_media",
    tabKey: "instagram_content_report",
    startDate: appliedStartDate,
    endDate: appliedEndDate,
    data: igContentNarrativePayload,
    promptVersion: "v6",
    enabled: !loading && !error && Boolean(kpis.length),
  });

  const contentTypeColorMap = useMemo(
    () =>
      new Map(
        postsByType.map((item, index) => [
          String(item.name).toLowerCase(),
          CONTENT_BREAKDOWN_COLORS[index % CONTENT_BREAKDOWN_COLORS.length],
        ])
      ),
    [postsByType]
  );

  const chartPostsByType = useMemo(
    () =>
      postsByType.map((item, index) => ({
        ...item,
        color: CONTENT_BREAKDOWN_COLORS[index % CONTENT_BREAKDOWN_COLORS.length],
      })),
    [postsByType]
  );

  const engagementCardRows = useMemo((): EngagementCardRow[] => {
    const rawRows: EngagementDetailRow[] = totalEngagements.map(row => ({
      metricKey: row.metricKey,
      value: row.value,
      valueFormattedValue: row.valueFormattedValue,
      percentageChange: row.percentageChange,
      percentageChangeFormattedValue: row.percentageChangeFormattedValue,
      comparisonLabel: row.comparisonLabel,
      trend: row.trend ?? [],
    }));

    const reactionRows = rawRows.filter(row =>
      INSTAGRAM_REACTION_KEYS.includes(row.metricKey)
    );
    const primaryRows = rawRows.filter(
      row => !INSTAGRAM_REACTION_KEYS.includes(row.metricKey)
    );

    if (reactionRows.length === 0) {
      return primaryRows;
    }

    // Individual reaction types rarely carry their own percentageChange — only
    // average the ones that actually have one, so a row full of "no data"
    // doesn't average out to a fabricated 0.
    const reactionChangesWithData = reactionRows
      .map(r => r.percentageChange)
      .filter((value): value is number => typeof value === "number" && Number.isFinite(value));
    const averagedReactionChange =
      reactionChangesWithData.length > 0
        ? reactionChangesWithData.reduce((sum, value) => sum + value, 0) /
          reactionChangesWithData.length
        : undefined;

    return [
      ...primaryRows.slice(0, 4),
      {
        metricKey: "reactions",
        // Client-summed across the individual reaction types below — the
        // backend never sends a single "total reactions" count, so there is
        // no FormattedValue sibling for this aggregate.
        value: reactionRows.reduce((sum, r) => sum + r.value, 0),
        percentageChange: reactionsPercentageChange ?? averagedReactionChange,
        // Only valid when the backend's own aggregate change is used — the
        // averaged fallback is client-derived and has no matching string.
        percentageChangeFormattedValue:
          reactionsPercentageChange !== undefined
            ? reactionsPercentageChangeFormattedValue
            : undefined,
        comparisonLabel: t("organicMedia.instagram.vsLastPeriod"),
        trend: [],
        details: reactionRows,
      },
      ...primaryRows.slice(4),
    ];
  }, [totalEngagements, reactionsPercentageChange, reactionsPercentageChangeFormattedValue, t]);

  const rightChartData = useMemo(
    () =>
      postTypeMetrics
        .map(row => ({
          type: row.type,
          value: row[selectedMetric],
          valueFormattedValue: getPostTypeMetricFormattedValue(row, selectedMetric),
          color:
            contentTypeColorMap.get(String(row.type).toLowerCase()) ??
            ORGANIC_CHART_COLORS[0],
        }))
        .sort((a, b) => (b.value ?? 0) - (a.value ?? 0)),
    [contentTypeColorMap, postTypeMetrics, selectedMetric]
  );

  const contentTypeBarTotalPages = Math.max(
    1,
    Math.ceil(rightChartData.length / CONTENT_TYPE_BAR_PAGE_SIZE)
  );
  const pagedRightChartData = useMemo(() => {
    const startIndex = (contentTypeBarPage - 1) * CONTENT_TYPE_BAR_PAGE_SIZE;
    return rightChartData.slice(startIndex, startIndex + CONTENT_TYPE_BAR_PAGE_SIZE);
  }, [contentTypeBarPage, rightChartData]);

  const rightChartAxisConfig = useMemo<PercentAxisConfig>(() => {
    if (!PERCENT_METRICS.includes(selectedMetric)) {
      return {};
    }

    return getPercentAxisConfig(rightChartData.map(row => Number(row.value ?? 0)));
  }, [rightChartData, selectedMetric]);

  const pieLegendItems = useMemo(() => {
    const total = chartPostsByType.reduce((sum, item) => sum + Number(item.value ?? 0), 0);
    if (!total) {
      return [];
    }

    return [...chartPostsByType].sort((a, b) => Number(b.value ?? 0) - Number(a.value ?? 0));
  }, [chartPostsByType]);

  const pieLegendTotal = useMemo(
    () => pieLegendItems.reduce((sum, item) => sum + Number(item.value ?? 0), 0),
    [pieLegendItems]
  );

  const trendComparisonCards = useMemo(() => {

    const score = (metricKey: string) => {
      if (metricKey.includes("impression")) {return 3;}
      if (metricKey.includes("reach")) {return 2;}
      if (metricKey.includes("engagement")) {return 1;}
      return 0;
    };

    const sorted = [...kpis].sort((a, b) => score(b.metricKey) - score(a.metricKey));
    return sorted.slice(0, 2);
  }, [kpis]);

  const trendComparisonData = useMemo(() => {
    const byDate = new Map<string, Record<string, string | number>>();

    trendComparisonCards.forEach(card => {
      card.trend.forEach(point => {
        const dateKey = trendGranularity === "monthly"
          ? getMonthBucket(point.date)
          : point.date;
        const row = byDate.get(dateKey) ?? { date: dateKey };
        row[card.title] = Number(row[card.title] ?? 0) + Number(point.value ?? 0);
        byDate.set(dateKey, row);
      });
    });

    return Array.from(byDate.values()).sort((a, b) => {
      const aMs = parseDateValueToMs(String(a.date ?? ""));
      const bMs = parseDateValueToMs(String(b.date ?? ""));
      if (Number.isNaN(aMs) || Number.isNaN(bMs)) {
        return String(a.date).localeCompare(String(b.date));
      }
      return aMs - bMs;
    });
  }, [trendComparisonCards, trendGranularity]);

  const trendLegendItems = useMemo(
    () =>
      trendComparisonCards.map((card, index) => ({
        label: formatTrendMetricLabel(card.title, t),
        color: ORGANIC_CHART_COLORS[index % ORGANIC_CHART_COLORS.length],
      })),
    [trendComparisonCards, t]
  );

  const handleRangeChange = (range: DateRange) => {
    setPostCaption("(All)");
    onApplyDates(range);
  };

  const filteredPostDetails = useMemo(() => {
    const query = postTableSearch.trim().toLowerCase();
    const startMs = parseDateValueToMs(appliedStartDate);
    const endMs = parseDateValueToMs(appliedEndDate, true);

    return postDetails.filter(row => {
      const rowDateMs = parseDateValueToMs(String(row.createdAt ?? ""));
      const isInSelectedRange =
        Number.isNaN(rowDateMs) ||
        Number.isNaN(startMs) ||
        Number.isNaN(endMs) ||
        (rowDateMs >= startMs && rowDateMs <= endMs);
      if (!isInSelectedRange) {
        return false;
      }
      if (!query) {
        return true;
      }
      const caption = String(row.caption ?? "").toLowerCase();
      return caption.includes(query);
    });
  }, [appliedEndDate, appliedStartDate, postDetails, postTableSearch]);

  const sortedPostDetails = useMemo(() => {
    if (!postTableSort) {
      return filteredPostDetails;
    }

    return [...filteredPostDetails].sort((left, right) => {
      switch (postTableSort.key) {
        case "caption":
          return compareDashboardValues(left.caption, right.caption, postTableSort.direction);
        case "postType":
          return compareDashboardValues(left.postType, right.postType, postTableSort.direction);
        case "impressions":
          return compareDashboardValues(left.impressions, right.impressions, postTableSort.direction);
        case "reach":
          return compareDashboardValues(left.reach, right.reach, postTableSort.direction);
        case "likes":
          return compareDashboardValues(left.likes, right.likes, postTableSort.direction);
        case "comments":
          return compareDashboardValues(left.comments, right.comments, postTableSort.direction);
        case "shares":
          return compareDashboardValues(left.shares, right.shares, postTableSort.direction);
        case "saves":
          return compareDashboardValues(left.saves, right.saves, postTableSort.direction);
        case "engagementRate":
          return compareDashboardValues(left.engagementRate, right.engagementRate, postTableSort.direction);
        default:
          return 0;
      }
    });
  }, [filteredPostDetails, postTableSort]);
  const totalPostPages = Math.max(1, Math.ceil(sortedPostDetails.length / POSTS_PER_PAGE));
  const paginatedPostDetails = useMemo(() => {
    const startIndex = (currentPostPage - 1) * POSTS_PER_PAGE;
    return sortedPostDetails.slice(startIndex, startIndex + POSTS_PER_PAGE);
  }, [currentPostPage, sortedPostDetails]);

  const {
    rows: exportPostDetails,
    onBeforeCapture: onBeforePostTableCapture,
    onAfterCapture: onAfterPostTableCapture,
  } = useDashboardPdfRowOverride(paginatedPostDetails, async () => sortedPostDetails);

  useEffect(() => {
    setCurrentPostPage(1);
  }, [appliedStartDate, appliedEndDate, postCaption, postDetails.length, postTableSort, postTableSearch]);

  useEffect(() => {
    if (currentPostPage > totalPostPages) {
      setCurrentPostPage(totalPostPages);
    }
  }, [currentPostPage, totalPostPages]);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (
        reactionMenuRef.current &&
        !reactionMenuRef.current.contains(event.target as Node)
      ) {
        setIsReactionMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, []);

  useEffect(() => {
    setContentTypeBarPage(1);
  }, [selectedMetric]);

  useEffect(() => {
    if (contentTypeBarPage > contentTypeBarTotalPages) {
      setContentTypeBarPage(contentTypeBarTotalPages);
    }
  }, [contentTypeBarPage, contentTypeBarTotalPages]);

  const handleExportPosts = () => {
    downloadCsv(
      `instagram_content_posts_${appliedStartDate}_to_${appliedEndDate}.csv`,
      [
        t("organicMedia.instagram.table.caption"),
        t("organicMedia.instagram.table.type"),
        t("organicMedia.instagram.table.impressions"),
        t("organicMedia.instagram.table.reach"),
        t("organicMedia.instagram.table.likes"),
        t("organicMedia.instagram.table.comments"),
        t("organicMedia.instagram.table.shares"),
        t("organicMedia.instagram.table.saves"),
        t("organicMedia.instagram.table.engRate"),
        t("organicMedia.instagram.table.link"),
      ],
      sortedPostDetails.map(row => [
        row.caption,
        formatOrganicPostTypeLabel(row.postType, t),
        row.impressions,
        row.reach,
        row.likes,
        row.comments,
        row.shares,
        row.saves,
        row.engagementRateFormattedValue ??
          `${formatNumber(row.engagementRate, { minimumFractionDigits: 1, maximumFractionDigits: 1 }, i18n.language)}%`,
        row.postUrl,
      ])
    );
  };

  return (
    <div
      className={`organic-content-report${isPdfCapturing ? " organic-content-report--pdf-capture" : ""}`}
      ref={pdfContainerRef}
    >
      <DashboardSectionHeader
        className="organic-content-report__section-header"
        title={t("organicMedia.tabs.instagramContent")}
        subtitle={t("organicMedia.contentReport.instagramSubtitle")}
        actionsClassName="dashboard-section-header__actions--filters"
        actions={
          <>
            <div className="dashboard-section-header__control dashboard-section-header__control--wide">
              <OrganicDateRangePicker
                startDate={draftStartDate}
                endDate={draftEndDate}
                onRangeChange={handleRangeChange}
              />
            </div>

            <div className="dashboard-section-header__control">
              <OrganicSelectMenu
                className="organic-content-report__caption-select"
                aria-label={t("organicMedia.common.postCaptionFilter")}
                value={postCaption}
                onChange={setPostCaption}
                options={postCaptions.map(option => ({
                  value: option,
                  label:
                    option === "(All)"
                      ? t("organicMedia.common.allCaptionsOption")
                      : truncateCaption(option),
                  title:
                    option === "(All)"
                      ? t("organicMedia.common.allCaptionsOption")
                      : getCaptionPreview(option),
                }))}
              />
            </div>
            <DashboardExportButton
              dashboardName="Instagram Content"
              dateRange={{ startDate: appliedStartDate, endDate: appliedEndDate }}
              containerRef={pdfContainerRef}
              disabled={loading || Boolean(error)}
              onBeforeCapture={async () => {
                setIsPdfCapturing(true);
                await onBeforePostTableCapture();
              }}
              onAfterCapture={() => {
                onAfterPostTableCapture();
                setIsPdfCapturing(false);
              }}
            />
          </>
        }
      />

      {error && (
        <div className="organic-tabs-dashboard__error" role="alert">
          {error}
        </div>
      )}

      <div data-pdf-block="narrative">
        <DashboardNarrativePanel
          narrative={igContentNarrative}
          loading={loading || igContentNarrativeLoading}
          aiSummary={aiSummary}
        />
      </div>

      {loading ? (
        <div className="organic-content-report__loading">
          <LoadingSpinner compact />
        </div>
      ) : (
        <>
          <div className="organic-content-report__kpis" data-pdf-block="kpi-cards">
            {kpis.map((card, index) => {
              const kpiTrendPct = resolveDashboardChangePercent(
                card.percentageChange,
                `instagram-content-kpi-${index}-${card.title}`,
                { treatZeroAsMissing: true }
              );
              const hasKpiTrend =
                typeof card.percentageChange === "number" &&
                Number.isFinite(card.percentageChange);
              return (
              <div key={card.title} className="organic-content-card">
                <div className="organic-content-card__header">
                  <div>
                    {(() => {
                      const definitionInfo = getOrganicContentKpiDefinitionInfo(
                        card.title,
                        (card as { tooltipLabel?: string }).tooltipLabel,
                        formatKpiTitle,
                        t,
                        "instagram",
                      );

                      return (
                        <div className="organic-content-card__title-row">
                          <h3>{formatKpiTitle(card.title, t)}</h3>
                          <DefinitionTooltip
                            label={formatKpiTitle(card.title, t)}
                            info={definitionInfo}
                            align={index >= 2 ? "end" : "start"}
                            formulaTitle={t("organicMedia.common.formula")}
                          />
                        </div>
                      );
                    })()}
                    <div className="organic-content-card__value">{card.value}</div>
                  </div>
                  <div className="organic-content-card__icon" aria-hidden>
                    {(() => {
                      const key =
                        Object.keys(KPI_ICON_MAP).find(candidate =>
                          card.title.toLowerCase().includes(candidate)
                        ) ?? "posts";
                      const Icon = KPI_ICON_MAP[key];
                      return <Icon size={20} />;
                    })()}
                  </div>
                </div>
                <div className="organic-content-card__footer">
                  {hasKpiTrend ? (
                    <div className="organic-content-card__period">
                      <span
                        className={`organic-content-card__change organic-content-card__change--${getTrendState(kpiTrendPct)}`}
                      >
                        {kpiTrendPct === 0 ? null : kpiTrendPct > 0 ? (
                          <TrendingUp size={13} aria-hidden />
                        ) : (
                          <TrendingDown size={13} aria-hidden />
                        )}
                        {card.percentageChangeFormattedValue ??
                          withBidiIsolate(
                            `${formatNumber(kpiTrendPct, { minimumFractionDigits: 2, maximumFractionDigits: 2 }, i18n.language)}%`,
                            i18n.language
                          )}
                      </span>
                      <span>{card.comparisonLabel}</span>
                    </div>
                  ) : null}
                </div>
              </div>
            );
            })}
          </div>

          <div className="organic-content-report__engagement-block" data-pdf-block="engagement">
            <div className="organic-content-report__engagement-grid">
              {engagementCardRows.map((row, index) => {
                const reactionDetails = row.details;
                const engagementLabel = formatInstagramEngagementLabel(row.metricKey, t);
                const engagementTrendPct = resolveDashboardChangePercent(
                  row.percentageChange,
                  `instagram-content-engagement-${row.metricKey}`,
                  { treatZeroAsMissing: true }
                );
                // A null change means the backend has no comparison for this
                // metric, so hide the badge entirely; a real `0` is a genuine
                // "no change" and is shown as "0.0%".
                const hasEngagementTrend =
                  typeof row.percentageChange === "number" &&
                  Number.isFinite(row.percentageChange);
                return (
                  <div
                    key={row.metricKey}
                    className={`organic-engagement-card ${
                      reactionDetails?.length ? "organic-engagement-card--dropdown" : ""
                    }`}
                    ref={reactionDetails?.length ? reactionMenuRef : undefined}
                  >
                    <div className="organic-engagement-card__label-row">
                      <div className="organic-engagement-card__label">
                        <span>{engagementLabel}</span>
                        <DefinitionTooltip
                          label={engagementLabel}
                          info={getOrganicEngagementDefinitionInfo(row.metricKey, engagementLabel, t, "instagram")}
                          align={index >= 3 ? "end" : "start"}
                          formulaTitle={t("organicMedia.common.formula")}
                        />
                      </div>
                      {reactionDetails?.length ? (
                        <button
                          type="button"
                          className="organic-engagement-card__toggle"
                          aria-label={t("organicMedia.common.breakdown", {
                            label: engagementLabel,
                          })}
                          aria-expanded={isReactionMenuOpen}
                          onClick={() => setIsReactionMenuOpen(open => !open)}
                        >
                          {isReactionMenuOpen ? (
                            <ChevronUp size={14} aria-hidden />
                          ) : (
                            <ChevronDown size={14} aria-hidden />
                          )}
                        </button>
                      ) : null}
                    </div>
                    <div className="organic-engagement-card__value">
                      {row.valueFormattedValue ?? formatNumber(row.value, {}, i18n.language)}
                    </div>
                    {hasEngagementTrend ? (
                      <div className="organic-engagement-card__period">
                        <span
                          className={`organic-engagement-card__change organic-engagement-card__change--${getTrendState(engagementTrendPct)}`}
                        >
                          {engagementTrendPct === 0 ? null : engagementTrendPct > 0 ? (
                            <TrendingUp size={13} aria-hidden />
                          ) : (
                            <TrendingDown size={13} aria-hidden />
                          )}
                          {row.percentageChangeFormattedValue ??
                            withBidiIsolate(
                              `${formatNumber(engagementTrendPct, { minimumFractionDigits: 1, maximumFractionDigits: 1 }, i18n.language)}%`,
                              i18n.language
                            )}
                        </span>
                        <span>{row.comparisonLabel}</span>
                      </div>
                    ) : null}
                    {reactionDetails?.length && isReactionMenuOpen ? (
                      <div className="organic-engagement-card__dropdown" role="menu">
                        {reactionDetails.map((detail: EngagementDetailRow) => {
                          const detailTrendPct = resolveDashboardChangePercent(
                            detail.percentageChange,
                            `instagram-content-engagement-detail-${detail.metricKey}`,
                            { treatZeroAsMissing: true }
                          );
                          const hasDetailTrend =
                            typeof detail.percentageChange === "number" &&
                            Number.isFinite(detail.percentageChange);
                          return (
                          <div
                            key={detail.metricKey}
                            className="organic-engagement-card__dropdown-row"
                          >
                            <div className="organic-engagement-card__dropdown-metric">
                              <span className="organic-engagement-card__dropdown-label">
                                {formatInstagramEngagementLabel(detail.metricKey, t)}
                              </span>
                              <strong className="organic-engagement-card__dropdown-value">
                                {detail.valueFormattedValue ?? formatNumber(detail.value, {}, i18n.language)}
                              </strong>
                            </div>
                            {hasDetailTrend ? (
                              <span
                                className={`organic-engagement-card__dropdown-trend organic-engagement-card__dropdown-trend--${getTrendState(detailTrendPct)}`}
                              >
                                {detailTrendPct === 0 ? null : detailTrendPct > 0 ? (
                                  <TrendingUp size={11} aria-hidden />
                                ) : (
                                  <TrendingDown size={11} aria-hidden />
                                )}
                                {detail.percentageChangeFormattedValue ??
                                  withBidiIsolate(
                                    `${formatNumber(detailTrendPct, { minimumFractionDigits: 1, maximumFractionDigits: 1 }, i18n.language)}%`,
                                    i18n.language
                                  )}
                              </span>
                            ) : null}
                          </div>
                        );
                        })}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="organic-content-report__chart-grid" data-pdf-block="charts">
            <div className="organic-content-panel organic-content-panel--content-breakdown">
              <div className="organic-content-panel__header">
                <div className="organic-content-panel__title">
                  <h3>{t("organicMedia.instagram.sections.contentTypeBreakdown")}</h3>
                  <DefinitionTooltip
                    label={t("organicMedia.instagram.sections.contentTypeBreakdown")}
                    info={getOrganicContentSectionDefinitionInfo("contentTypeBreakdown", t)}
                    formulaTitle={t("organicMedia.common.formula")}
                  />
                </div>
              </div>
              <div className="organic-content-panel__donut">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Tooltip
                      content={<DonutTooltip />}
                      allowEscapeViewBox={{ x: false, y: true }}
                      wrapperStyle={{ pointerEvents: "none" }}
                    />
                    <Pie
                      data={chartPostsByType}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius="58%"
                      outerRadius="86%"
                      startAngle={90}
                      endAngle={-270}
                      paddingAngle={0}
                      labelLine={false}
                    >
                      {chartPostsByType.map(slice => (
                        <Cell key={slice.name} fill={slice.color} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
                <div className="organic-content-panel__donut-center">
                  <span className="organic-content-panel__donut-center-value">
                    {formatNumber(pieLegendTotal, {}, i18n.language)}
                  </span>
                  <span className="organic-content-panel__donut-center-label">
                    {t("organicMedia.contentReport.totalPosts")}
                  </span>
                </div>
              </div>
              <div
                className={`organic-content-panel__pie-legend ${
                  pieLegendItems.length > 3
                    ? "organic-content-panel__pie-legend--columns"
                    : "organic-content-panel__pie-legend--centered"
                }`}
                aria-hidden
              >
                {pieLegendItems.map(item => (
                  <div
                    key={`${item.name}-${item.value}`}
                    className="organic-content-panel__pie-legend-item"
                    >
                      <span
                        className="organic-content-panel__pie-legend-swatch"
                        style={{ backgroundColor: item.color }}
                      />
                      <span className="organic-content-panel__pie-legend-text">
                        {formatOrganicPostTypeLabel(item.name, t)}
                      </span>
                      <strong>
                        {item.valueFormattedValue ?? formatNumber(Number(item.value ?? 0), {}, i18n.language)}
                        {pieLegendTotal > 0
                          ? withBidiIsolate(
                              ` (${formatNumber((Number(item.value ?? 0) / pieLegendTotal) * 100, { minimumFractionDigits: 1, maximumFractionDigits: 1 }, i18n.language)}%)`,
                              i18n.language
                            )
                          : ""}
                      </strong>
                    </div>
                ))}
              </div>
            </div>

            <div className="organic-content-panel organic-content-panel--bar">
              <div className="organic-content-panel__header">
                <div className="organic-content-panel__title">
                  <h3>{t("organicMedia.instagram.sections.contentPerformanceByType")}</h3>
                  <DefinitionTooltip
                    label={t("organicMedia.instagram.sections.contentPerformanceByType")}
                    info={getOrganicContentSectionDefinitionInfo("contentPerformanceByType", t)}
                    formulaTitle={t("organicMedia.common.formula")}
                  />
                </div>
                <div className="organic-bars__select-wrap">
                  <OrganicSelectMenu
                    className="organic-bars__select"
                    ariaLabel={t("organicMedia.contentReport.engagementMetricSelectAria")}
                    value={selectedMetric}
                    onChange={value => setSelectedMetric(value as MetricKey)}
                    options={[
                      { value: "engagementRate", label: t("organicMedia.instagram.metrics.engagementRate") },
                      { value: "organicReach", label: t("organicMedia.instagram.metrics.reach") },
                      { value: "impressions", label: t("organicMedia.instagram.metrics.impressions") },
                    ]}
                  />
                </div>
              </div>
              <div className="organic-content-panel__chart-fill">
              <ResponsiveContainer width="100%" height={340}>
                <BarChart
                  data={pagedRightChartData}
                  margin={
                    isRTL
                      ? { top: 24, right: 12, left: 6, bottom: 12 }
                      : { top: 24, right: 6, left: 12, bottom: 12 }
                  }
                >
                  <CartesianGrid stroke="#e4e8ef" strokeDasharray="4 4" vertical={false} />
                  <XAxis
                    dataKey="type"
                    reversed={isRTL}
                    tick={<ContentTypeAxisTick />}
                    axisLine={{ stroke: "#d7dde7" }}
                    tickLine={false}
                    tickMargin={14}
                    interval={0}
                    height={20}
                  />
                  <YAxis
                    orientation={isRTL ? "right" : "left"}
                    width={70}
                    tickMargin={isRTL ? 10 : 4}
                    tick={(tickProps: { x?: number; y?: number; payload?: { value: number } }) => {
                      const { x = 0, y = 0, payload } = tickProps;
                      return (
                        <text
                          x={x}
                          y={y}
                          dy={4}
                          // Recharts anchors a right-side axis tick with `start` (text grows
                          // away from the plot, to the right) — but SVG `text-anchor` reads
                          // its start/end edge off the inherited `direction`, and this text
                          // sits inside an Arabic `dir="rtl"` page, which flips it and sends
                          // the label growing back over the axis line instead. Pin `direction`
                          // to ltr so the anchor means what Recharts intended regardless.
                          textAnchor={isRTL ? "start" : "end"}
                          direction="ltr"
                          fill="#5e6a7d"
                          fontSize={13}
                        >
                          {formatBarMetricTick(selectedMetric, payload?.value ?? 0, i18n.language)}
                        </text>
                      );
                    }}
                    axisLine={{ stroke: "#9aa5b8" }}
                    tickLine={{ stroke: "#9aa5b8" }}
                    domain={rightChartAxisConfig.domain}
                    ticks={rightChartAxisConfig.ticks}
                  />
                  <Tooltip
                    content={<HorizontalBarTooltip metricKey={selectedMetric} />}
                    cursor={{ fill: "rgba(109, 40, 217, 0.08)" }}
                    allowEscapeViewBox={{ x: false, y: true }}
                    wrapperStyle={{ pointerEvents: "none" }}
                  />
                  <Bar dataKey="value" radius={[6, 6, 0, 0]} barSize={36}>
                    <LabelList
                      dataKey="value"
                      position="top"
                      dy={-8}
                      formatter={(value: number | string) => formatBarMetricTick(selectedMetric, value, i18n.language)}
                      className="organic-bars__value-label"
                    />
                    {pagedRightChartData.map(entry => (
                      <Cell key={`${entry.type}-${selectedMetric}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            {rightChartData.length > 0 ? (
              <div className="organic-bars__pagination">
                <button
                  type="button"
                  onClick={() => setContentTypeBarPage(page => Math.max(1, page - 1))}
                  disabled={contentTypeBarPage === 1}
                  aria-label={t("organicMedia.common.previousPage")}
                >
                  {isRTL ? "›" : "‹"}
                </button>
                <span>
                  {t("pagination.pageOf", {
                    current: formatNumber(contentTypeBarPage, {}, i18n.language),
                    total: formatNumber(contentTypeBarTotalPages, {}, i18n.language),
                  })}
                </span>
                <button
                  type="button"
                  onClick={() =>
                    setContentTypeBarPage(page =>
                      Math.min(contentTypeBarTotalPages, page + 1)
                    )
                  }
                  disabled={contentTypeBarPage === contentTypeBarTotalPages}
                  aria-label={t("organicMedia.common.nextPage")}
                >
                  {isRTL ? "‹" : "›"}
                </button>
              </div>
            ) : null}
            </div>

            <div className="organic-content-panel">
              <div className="organic-content-panel__header">
                <div className="organic-content-panel__title">
                  <h3>{t("organicMedia.instagram.sections.trends30d")}</h3>
                  <DefinitionTooltip
                    label={t("organicMedia.instagram.sections.trends30d")}
                    info={getOrganicContentSectionDefinitionInfo("trendsLast30Days", t)}
                    formulaTitle={t("organicMedia.common.formula")}
                  />
                </div>
                <div
                  className="organic-content-panel__segmented"
                  role="group"
                  aria-label={t("organicMedia.contentReport.trendGranularity")}
                >
                  {(["daily", "monthly"] as TrendGranularity[]).map(option => (
                    <button
                      key={option}
                      type="button"
                      className={
                        trendGranularity === option
                          ? "organic-content-panel__segmented-button organic-content-panel__segmented-button--active"
                          : "organic-content-panel__segmented-button"
                      }
                      onClick={() => setTrendGranularity(option)}
                    >
                      {t(`organicMedia.contentReport.${option}`)}
                    </button>
                  ))}
                </div>
              </div>
              <ResponsiveContainer width="100%" height={380}>
                <LineChart data={trendComparisonData} margin={{ top: 4, right: 4, left: -4, bottom: 0 }}>
                  <CartesianGrid stroke="#e4e8ef" strokeDasharray="4 4" />
                  <XAxis
                    dataKey="date"
                    tick={{ fill: "#5e6a7d", fontSize: 12 }}
                    tickFormatter={value => formatTrendDateByGranularity(value, trendGranularity, i18n.language)}
                    axisLine={{ stroke: "#d7dde7" }}
                    tickLine={false}
                    interval="preserveStartEnd"
                    height={30}
                  />
                  <YAxis
                    yAxisId="pct"
                    hide={!trendComparisonCards.some(card => isPercentTrendCard(card))}
                    tick={{ fill: "#5e6a7d", fontSize: 12 }}
                    axisLine={{ stroke: "#d7dde7" }}
                    tickLine={false}
                    width={38}
                  />
                  <YAxis
                    yAxisId="count"
                    orientation="right"
                    hide={!trendComparisonCards.some(card => !isPercentTrendCard(card))}
                    tick={{ fill: "#5e6a7d", fontSize: 12 }}
                    axisLine={{ stroke: "#d7dde7" }}
                    tickLine={false}
                    width={48}
                    tickFormatter={value => formatNumber(Number(value), {}, i18n.language)}
                  />
                  <Tooltip
                    content={<OverTimeTooltip />}
                    labelFormatter={(value: string | number) => formatTrendDateByGranularity(value, trendGranularity, i18n.language)}
                    allowEscapeViewBox={{ x: false, y: false }}
                    wrapperStyle={{ pointerEvents: "none" }}
                    position={{ y: 18 }}
                  />
                  {trendComparisonCards.map((card, index) => (
                    <Line
                      key={card.title}
                      yAxisId={isPercentTrendCard(card) ? "pct" : "count"}
                      type="monotone"
                      dataKey={card.title}
                      name={formatTrendMetricLabel(card.title, t)}
                      stroke={ORGANIC_CHART_COLORS[index % ORGANIC_CHART_COLORS.length]}
                      strokeWidth={2.5}
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
              <div className="organic-content-panel__trend-legend" aria-hidden>
                {trendLegendItems.map(item => (
                  <div key={item.label} className="organic-content-panel__trend-legend-item">
                    <span
                      className="organic-content-panel__trend-legend-swatch"
                      style={{ "--legend-color": item.color } as CSSProperties}
                    />
                    <span>{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
            </div>

          <div
            className="organic-content-report__post-table-block"
            data-pdf-block="post-table"
            data-pdf-splittable
          >
            <div className="organic-post-details organic-post-details--modern">
              <DashboardDataTablePanel>
              <DashboardDataTablePanelHeader>
                <div className="organic-content-panel__title">
                  <h3>{t("organicMedia.instagram.sections.postLevelPerformance")}</h3>
                  <DefinitionTooltip
                    label={t("organicMedia.instagram.sections.postLevelPerformance")}
                    info={getOrganicContentSectionDefinitionInfo("postLevelPerformance", t)}
                    formulaTitle={t("organicMedia.common.formula")}
                  />
                </div>
                <div className="organic-post-details__table-toolbar">
                  <DashboardDataTableSearch
                    value={postTableSearch}
                    onChange={event => setPostTableSearch(event.target.value)}
                    placeholder={t("organicMedia.contentReport.searchPostsByCaption")}
                  />
                  <button
                    type="button"
                    className="organic-post-details__export-button"
                    onClick={handleExportPosts}
                    disabled={sortedPostDetails.length === 0}
                  >
                    <Download size={14} aria-hidden />
                    {t("organicMedia.contentReport.exportCsv")}
                  </button>
                </div>
              </DashboardDataTablePanelHeader>
              <DashboardDataTableScroll className="organic-post-details__table-wrap">
                <DashboardDataTableTable className="organic-post-details__table organic-post-details__table--modern">
                  <colgroup>
                    <col className="organic-post-details__col-post" />
                    <col className="organic-post-details__col-metric" />
                    <col className="organic-post-details__col-metric" />
                    <col className="organic-post-details__col-metric" />
                    <col className="organic-post-details__col-metric" />
                    <col className="organic-post-details__col-metric" />
                    <col className="organic-post-details__col-metric" />
                    <col className="organic-post-details__col-metric" />
                    <col className="organic-post-details__col-action" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th className="organic-post-details__th-post">
                        <DashboardDataTableSortButton onClick={() => setPostTableSort(current => toggleTableSort(current, "caption"))}>
                          {t("organicMedia.instagram.table.post")?.toUpperCase()}
                        </DashboardDataTableSortButton>
                      </th>
                      <th className="organic-post-details__th-tooltip">
                        <span className={dt.thHelpRow}>
                          <DashboardDataTableSortButton onClick={() => setPostTableSort(current => toggleTableSort(current, "impressions"))}>
                            {t("organicMedia.instagram.table.impressions")?.toUpperCase()}
                          </DashboardDataTableSortButton>
                          <DefinitionTooltip
                            label={t("organicMedia.instagram.table.impressions")}
                            info={getOrganicPlatformMetricDefinitionInfo("instagram", "impressions", t("organicMedia.instagram.table.impressions"), t)}
                            formulaTitle={t("organicMedia.common.formula")}
                          />
                        </span>
                      </th>
                      <th className="organic-post-details__th-tooltip">
                        <span className={dt.thHelpRow}>
                          <DashboardDataTableSortButton onClick={() => setPostTableSort(current => toggleTableSort(current, "reach"))}>
                            {t("organicMedia.instagram.table.reach")?.toUpperCase()}
                          </DashboardDataTableSortButton>
                          <DefinitionTooltip
                            label={t("organicMedia.instagram.table.reach")}
                            info={getOrganicPlatformMetricDefinitionInfo("instagram", "reach", t("organicMedia.instagram.table.reach"), t)}
                            formulaTitle={t("organicMedia.common.formula")}
                          />
                        </span>
                      </th>
                      <th className="organic-post-details__th-tooltip">
                        <span className={dt.thHelpRow}>
                          <DashboardDataTableSortButton onClick={() => setPostTableSort(current => toggleTableSort(current, "likes"))}>
                            {t("organicMedia.instagram.table.likes")?.toUpperCase()}
                          </DashboardDataTableSortButton>
                          <DefinitionTooltip
                            label={t("organicMedia.instagram.table.likes")}
                            info={getOrganicPlatformMetricDefinitionInfo("instagram", "likes", t("organicMedia.instagram.table.likes"), t)}
                            formulaTitle={t("organicMedia.common.formula")}
                          />
                        </span>
                      </th>
                      <th className="organic-post-details__th-tooltip">
                        <span className={dt.thHelpRow}>
                          <DashboardDataTableSortButton onClick={() => setPostTableSort(current => toggleTableSort(current, "comments"))}>
                            {t("organicMedia.instagram.table.comments")?.toUpperCase()}
                          </DashboardDataTableSortButton>
                          <DefinitionTooltip
                            label={t("organicMedia.instagram.table.comments")}
                            info={getOrganicPlatformMetricDefinitionInfo("instagram", "comments", t("organicMedia.instagram.table.comments"), t)}
                            formulaTitle={t("organicMedia.common.formula")}
                          />
                        </span>
                      </th>
                      <th className="organic-post-details__th-tooltip">
                        <span className={dt.thHelpRow}>
                          <DashboardDataTableSortButton onClick={() => setPostTableSort(current => toggleTableSort(current, "shares"))}>
                            {t("organicMedia.instagram.table.shares")?.toUpperCase()}
                          </DashboardDataTableSortButton>
                          <DefinitionTooltip
                            label={t("organicMedia.instagram.table.shares")}
                            info={getOrganicPlatformMetricDefinitionInfo("instagram", "shares", t("organicMedia.instagram.table.shares"), t)}
                            align="end"
                            formulaTitle={t("organicMedia.common.formula")}
                          />
                        </span>
                      </th>
                      <th className="organic-post-details__th-tooltip">
                        <span className={dt.thHelpRow}>
                          <DashboardDataTableSortButton onClick={() => setPostTableSort(current => toggleTableSort(current, "saves"))}>
                            {t("organicMedia.instagram.table.saves")?.toUpperCase()}
                          </DashboardDataTableSortButton>
                          <DefinitionTooltip
                            label={t("organicMedia.instagram.table.saves")}
                            info={getOrganicPlatformMetricDefinitionInfo("instagram", "saves", t("organicMedia.instagram.table.saves"), t)}
                            align="end"
                            formulaTitle={t("organicMedia.common.formula")}
                          />
                        </span>
                      </th>
                      <th className="organic-post-details__th-tooltip">
                        <span className={dt.thHelpRow}>
                          <DashboardDataTableSortButton onClick={() => setPostTableSort(current => toggleTableSort(current, "engagementRate"))}>
                            {t("organicMedia.instagram.table.engRate")?.toUpperCase()}
                          </DashboardDataTableSortButton>
                          <DefinitionTooltip
                            label={t("organicMedia.instagram.table.engRate")}
                            info={getOrganicPlatformMetricDefinitionInfo("instagram", "engagementRate", t("organicMedia.instagram.table.engRate"), t)}
                            align="end"
                            formulaTitle={t("organicMedia.common.formula")}
                          />
                        </span>
                      </th>
                      <th>{t("organicMedia.instagram.table.link")?.toUpperCase()}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {exportPostDetails.map((row, index) => (
                      <tr key={`${row.postSystemId || row.caption}-${index}`}>
                        <td>
                          <div className="organic-post-details__post-cell">
                            <div className="organic-post-details__thumb" aria-hidden>
                              <PostMediaThumb thumbnailUrl={row.thumbnailUrl} platform="instagram" />
                            </div>
                            <div className="organic-post-details__post-copy">
                              <div className="organic-post-details__caption-wrap">
                                <strong>{truncateCaption(row.caption, 48)}</strong>
                                <div className="organic-post-details__caption-tooltip">{getCaptionPreview(row.caption)}</div>
                              </div>
                              <div className="organic-post-details__tag-row">
                                <span className="organic-post-details__mini-tag organic-post-details__mini-tag--platform">
                                  {t("organicMedia.platform.instagram")}
                                </span>
                                <span className="organic-post-details__mini-tag organic-post-details__mini-tag--type">
                                  {formatOrganicPostTypeLabel(row.postType, t)}
                                </span>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>{row.impressionsFormattedValue ?? formatNumber(row.impressions, {}, i18n.language)}</td>
                        <td>{row.reachFormattedValue ?? formatNumber(row.reach, {}, i18n.language)}</td>
                        <td>{row.likesFormattedValue ?? formatNumber(row.likes, {}, i18n.language)}</td>
                        <td>{row.commentsFormattedValue ?? formatNumber(row.comments, {}, i18n.language)}</td>
                        <td>{row.sharesFormattedValue ?? formatNumber(row.shares, {}, i18n.language)}</td>
                        <td>{row.savesFormattedValue ?? formatNumber(row.saves, {}, i18n.language)}</td>
                        <td>
                          {row.engagementRateFormattedValue ??
                            withBidiIsolate(
                              `${formatNumber(row.engagementRate, { minimumFractionDigits: 1, maximumFractionDigits: 1 }, i18n.language)}%`,
                              i18n.language
                            )}
                        </td>
                        <td className="organic-post-details__action-cell">
                          {row.postUrl ? (
                            <a
                              className="organic-post-details__action-button"
                              href={row.postUrl}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <ExternalLink size={14} aria-hidden />
                              {t("organicMedia.instagram.table.viewPost")}
                            </a>
                          ) : (
                            <button
                              type="button"
                              className="organic-post-details__action-button"
                              disabled
                              title={t("organicMedia.contentReport.postLinkUnavailable")}
                            >
                              <ExternalLink size={14} aria-hidden />
                              {t("organicMedia.instagram.table.viewPost")}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </DashboardDataTableTable>
              </DashboardDataTableScroll>
              <TablePagePagination
                currentPage={currentPostPage}
                totalPages={totalPostPages}
                onPrevious={() => setCurrentPostPage(page => Math.max(1, page - 1))}
                onNext={() => setCurrentPostPage(page => Math.min(totalPostPages, page + 1))}
                className="organic-post-details__pager"
              />
              </DashboardDataTablePanel>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default InstagramContentReport;
